import { Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import TimerIcon from "../../../../assets/icons/mingcute_time-line.svg";
import ziplyRouterImage from "../../../../assets/images/ziply-router.png";
import Button from "../../../../components/Button/Button";
import { ErrorComponent } from "../../../../components/ErrorComponent/ErrorComponent";
import { Spinner } from "../../../../components/shared/LoadingSpinner/Spinner";
import {
  ACCOUNT_SUMMARY_CURRENT_BALANCE_TEXT,
  ACCOUNT_SUMMARY_DISCONNECTED_ACCOUNT,
  ACCOUNT_SUMMARY_ENROLL,
  ACCOUNT_SUMMARY_MANAGE_AUTOPAY,
  ACCOUNT_SUMMARY_SETUP_AUTOPAY,
  ACCOUNT_SUMMARY_VIEW_YOUR_BILL,
  DEFAULT_AUTOPAY_PAPERLESS_DISCOUNT_MESSAGES,
  FETCHING_BILLING_DETAILS_MESSAGE,
  YOUR_PLAN,
} from "../../../../Constants/constants";
import {
  formatNumberWithCommas,
  getBannerMessageByKey,
  getFlags,
} from "../../../../Services/helper";
import {
  AccountSummaryActionButtonContainer,
  AccountSummaryActionButtonsSection,
  AccountSummaryBalanceText,
  AccountSummaryCardContainer,
  AccountSummaryContentSection,
  AccountSummaryDiscountEnroll,
  AccountSummaryDiscountIconOff,
  AccountSummaryDiscountIconOn,
  AccountSummaryDiscountLabel,
  AccountSummaryDiscountSection,
  AccountSummaryDiscountValue,
  AccountSummaryPaymentMessageRow,
  AccountSummaryPlanHeading,
  AccountSummaryPlanNameText,
  AccountSummaryRecurringBillBox,
  AccountSummaryRouterImage,
  AccountSummarySaveMoreLink,
  AccountSummarySurchargeWrapper,
  DiscountRowBox,
  IconLabelBox,
  ImageContainerBox,
  StyledCard,
} from "../../../../Styles/GlobalStyles";
import { Theme } from "../../../../Styles/GlobalTheme";
import Surcharge from "../../../Billing/Components/Surcharge/Surcharge";
import { RecurringBillDueDate } from "../RecurringBillStatus/RecurringBillDueDate";

export const AccountSummaryCard = ({
  billingLoading,
  autopayPaperlessLoading,
  billingError,
  getbillingDetails,
}: {
  billingLoading?: boolean;
  autopayPaperlessLoading?: boolean;
  billingError?: boolean;
  getbillingDetails?: () => void;
}) => {
  const isAdmin = process.env.REACT_APP_CXP_ADMIN_ACCESS_ENABLED;
  const navigate = useNavigate();
  const activeAccount = useSelector((state: any) => state?.customerInfoDetails?.activeAccount);
  const {
    ziplyPlanName,
    currentDueBalance,
    paymentConfirmationMessage,
    autopaySurchargeDetails,
    showAutopay,
    error,
    isSMB,
    showAutopayDiscountMessage,
  } = useSelector((state: any) => state?.customerPlanDetails);
  const { autopayAmount, paperlessAmount, isNewCustomer } = useSelector(
    (state: any) => state?.customerPlanDetails
  );
  const { autoPayData, paperLessData } = useSelector(
    (state: any) => state?.autopayPaperlessDetails
  );
  const sideCoreBannerMessages = useSelector(
    (state: any) => state?.sideCoreContent?.bannerMessages
  );
  const sidecoreDiscountMessages = getBannerMessageByKey(
    "autopay-paperless-discount-messages",
    sideCoreBannerMessages,
    false
  );
  const autopayPaperlessDiscountMessages = Object.fromEntries(
    Object.keys(DEFAULT_AUTOPAY_PAPERLESS_DISCOUNT_MESSAGES).map((key) => [
      key,
      sidecoreDiscountMessages?.[key] || DEFAULT_AUTOPAY_PAPERLESS_DISCOUNT_MESSAGES[key],
    ])
  );
  const envFlags = useSelector((state: any) => state?.userInfo?.envFlags);
  const ENABLE_SURCHARGE = getFlags("enable_surcharge", envFlags);
  const isAutopayOn = !!(autoPayData && Object.keys(autoPayData).length > 0);
  const isCreditCardAutopay =
    isAutopayOn && autoPayData?.autoPayEnrolledPaymentMethod?.toLowerCase?.() === "credit card";
  const paperlessFlag = paperLessData?.paperlessFlag;
  const isPaperlessOn = paperlessFlag === true || paperlessFlag === "true";
  const showDiscountRows = activeAccount && !isNewCustomer;
  const autopayDiscountLabel = isAutopayOn
    ? isSMB || !showAutopayDiscountMessage
      ? autopayPaperlessDiscountMessages["autopay-enrolled-others"]
      : isCreditCardAutopay
        ? autopayPaperlessDiscountMessages["autopay-credit-discount-residential"]
        : autopayPaperlessDiscountMessages["autopay-enrolled-residential"]
    : isSMB || !showAutopayDiscountMessage
      ? autopayPaperlessDiscountMessages["autopay-not-enrolled-others"]
      : autopayPaperlessDiscountMessages["autopay-not-enrolled-residential"];
  const paperlessDiscountLabel = isPaperlessOn
    ? isSMB || !showAutopayDiscountMessage
      ? autopayPaperlessDiscountMessages["paperless-enrolled-others"]
      : autopayPaperlessDiscountMessages["paperless-enrolled-residential"]
    : isSMB || !showAutopayDiscountMessage
      ? autopayPaperlessDiscountMessages["paperless-not-enrolled-others"]
      : autopayPaperlessDiscountMessages["paperless-not-enrolled-residential"];

  const autopayCreditDiscountValue =
    autopayPaperlessDiscountMessages["autopay-credit-discount-value"];
  const autopayCreditDiscountActionButtonLabel =
    autopayPaperlessDiscountMessages["autopay-credit-save-more-action-button"];

  const whiteColor = Theme.palette.customcolor.whiteColor as string;
  const cardHeadingColor = Theme.palette.customcolor.cardHeading;

  // Button handlers
  const handleManageAutoPay = () => {
    navigate({ pathname: "/billing/manageautopay" }, { state: { home: true } });
  };

  const handleEnrollAutopay = () => {
    navigate("/billing/signupautopay", { state: { NavigateToHome: true } });
  };

  const handleEnrollPaperless = () => {
    navigate({ pathname: "/billing", hash: "#autopayPaperless" });
  };

  const handleSaveMoreClick = () => {
    navigate(
      { pathname: "/billing/manageautopay" },
      { state: { home: true, openEditAutopay: true } }
    );
  };

  const handleNavigateCurrentBill = () => {
    navigate({ pathname: "/billing", hash: "#currentBill" });
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const handleRefresh = () => {
    getbillingDetails?.();
  };

  return (
    <>
      <AccountSummaryPlanHeading>{YOUR_PLAN}</AccountSummaryPlanHeading>
      {billingLoading ? (
        <StyledCard padding="editautopay">
          <Spinner loadingText={FETCHING_BILLING_DETAILS_MESSAGE} />
        </StyledCard>
      ) : error || billingError ? (
        <StyledCard padding="editautopay">
          <ErrorComponent onRefresh={handleRefresh} data-testid="error-message" />
        </StyledCard>
      ) : (
        <>
          <AccountSummaryCardContainer>
            {/* Left side content */}
            <AccountSummaryContentSection>
              <AccountSummaryPlanNameText
                fontSize={{ xs: "0.875rem", sm: "1rem" }}
                lineHeight={{ xs: "1.1rem", sm: "1.25rem" }}
                color={activeAccount === false ? cardHeadingColor : whiteColor}
                data-test-id="plan-name">
                {activeAccount === false ? ACCOUNT_SUMMARY_DISCONNECTED_ACCOUNT : ziplyPlanName}
              </AccountSummaryPlanNameText>

              <AccountSummaryBalanceText
                fontSize={{ xs: "1.5rem", sm: "1.5rem" }}
                fontWeight={"bold"}
                color={whiteColor}
                data-test-id="price">
                {formatNumberWithCommas(currentDueBalance ?? 0)}
                &nbsp;
                <Typography
                  component={"span"}
                  fontSize="13px"
                  lineHeight="15px"
                  fontWeight={400}
                  color={whiteColor}>
                  {ACCOUNT_SUMMARY_CURRENT_BALANCE_TEXT}
                </Typography>
              </AccountSummaryBalanceText>

              {paymentConfirmationMessage && (
                <AccountSummaryPaymentMessageRow
                  direction={"row"}
                  spacing={1}
                  alignItems={"center"}>
                  <img src={TimerIcon} alt="timer" />
                  <Typography
                    color={Theme.palette.customcolor.whiteColor}
                    fontSize={{ xs: "14px", sm: "16px" }}>
                    {paymentConfirmationMessage}
                  </Typography>
                </AccountSummaryPaymentMessageRow>
              )}

              {showDiscountRows && (
                <>
                  {
                    <AccountSummaryDiscountSection>
                      {autopayDiscountLabel && (
                        <DiscountRowBox>
                          <IconLabelBox>
                            {isAutopayOn ? (
                              <AccountSummaryDiscountIconOn />
                            ) : (
                              <AccountSummaryDiscountIconOff />
                            )}
                            <AccountSummaryDiscountLabel>
                              {autopayDiscountLabel}
                            </AccountSummaryDiscountLabel>
                          </IconLabelBox>
                          {isAutopayOn ? (
                            !isSMB &&
                            showAutopayDiscountMessage && (
                              <AccountSummaryDiscountValue>
                                {isCreditCardAutopay && autopayCreditDiscountValue ? (
                                  <>
                                    {`$${autopayCreditDiscountValue} `}
                                    {autopayCreditDiscountActionButtonLabel && (
                                      <AccountSummarySaveMoreLink onClick={handleSaveMoreClick}>
                                        {autopayCreditDiscountActionButtonLabel}
                                      </AccountSummarySaveMoreLink>
                                    )}
                                  </>
                                ) : (
                                  autopayAmount && `$${autopayAmount}`
                                )}
                              </AccountSummaryDiscountValue>
                            )
                          ) : (
                            <AccountSummaryDiscountEnroll onClick={handleEnrollAutopay}>
                              {ACCOUNT_SUMMARY_ENROLL}
                            </AccountSummaryDiscountEnroll>
                          )}
                        </DiscountRowBox>
                      )}

                      {paperlessDiscountLabel && (
                        <DiscountRowBox>
                          <IconLabelBox>
                            {isPaperlessOn ? (
                              <AccountSummaryDiscountIconOn />
                            ) : (
                              <AccountSummaryDiscountIconOff />
                            )}
                            <AccountSummaryDiscountLabel>
                              {paperlessDiscountLabel}
                            </AccountSummaryDiscountLabel>
                          </IconLabelBox>
                          {isPaperlessOn ? (
                            !isSMB &&
                            showAutopayDiscountMessage && (
                              <AccountSummaryDiscountValue>
                                {paperlessAmount && `$${paperlessAmount}`}
                              </AccountSummaryDiscountValue>
                            )
                          ) : (
                            <AccountSummaryDiscountEnroll onClick={handleEnrollPaperless}>
                              {ACCOUNT_SUMMARY_ENROLL}
                            </AccountSummaryDiscountEnroll>
                          )}
                        </DiscountRowBox>
                      )}
                    </AccountSummaryDiscountSection>
                  }
                  <AccountSummaryDiscountSection>
                    <AccountSummaryRecurringBillBox>
                      <RecurringBillDueDate />
                    </AccountSummaryRecurringBillBox>

                    {activeAccount &&
                      showAutopay &&
                      !isNewCustomer &&
                      !autopayPaperlessLoading &&
                      autoPayData &&
                      Object.keys(autoPayData)?.length > 0 &&
                      ENABLE_SURCHARGE &&
                      autopaySurchargeDetails?.surchargeEligible &&
                      autoPayData?.paymentMethodType.toLowerCase() === "tokenized-credit-card" && (
                        <AccountSummarySurchargeWrapper>
                          <Surcharge bannerText={autopaySurchargeDetails?.homeScreenMessage} />
                        </AccountSummarySurchargeWrapper>
                      )}
                  </AccountSummaryDiscountSection>
                </>
              )}

              <AccountSummaryActionButtonsSection>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  {activeAccount && showAutopay && (
                    <AccountSummaryActionButtonContainer>
                      <Button
                        type="pay"
                        title={
                          isAutopayOn
                            ? ACCOUNT_SUMMARY_MANAGE_AUTOPAY
                            : ACCOUNT_SUMMARY_SETUP_AUTOPAY
                        }
                        disabled={isAdmin === "TRUE"}
                        onClick={isAutopayOn ? handleManageAutoPay : handleEnrollAutopay}
                      />
                    </AccountSummaryActionButtonContainer>
                  )}
                  <AccountSummaryActionButtonContainer>
                    <Button
                      type="payment"
                      title={ACCOUNT_SUMMARY_VIEW_YOUR_BILL}
                      onClick={handleNavigateCurrentBill}
                    />
                  </AccountSummaryActionButtonContainer>
                </Stack>
              </AccountSummaryActionButtonsSection>
            </AccountSummaryContentSection>

            {/* Right side image - visible on sm and larger screens */}
            <ImageContainerBox>
              <AccountSummaryRouterImage src={ziplyRouterImage} alt="Ziply Fiber Router" />
            </ImageContainerBox>
          </AccountSummaryCardContainer>
        </>
      )}
    </>
  );
};
